import { useEffect, useState, type RefObject } from "react";

import type { MayaAssistantComposer } from "../context/sidebar-context";

/** Space left between the launcher and what it rises above. */
const GAP_PX = 12;
/** Never pushed closer than this to the top of the screen. */
const TOP_MARGIN_PX = 16;
/** How often the composer is re-measured: it grows with attachments and
 *  Maya's forms, and is replaced by another one when a chat starts — none of
 *  which a ResizeObserver on one element would catch. */
const MEASURE_MS = 500;

type Box = Pick<DOMRect, "top" | "bottom" | "left" | "right">;

/**
 * How far a launcher at (`x`, `y`) of `size` has to rise to clear every box
 * in its column: it climbs past each one it would touch, lowest first, and
 * stops `TOP_MARGIN_PX` from the top of the screen.
 */
export const liftToClear = (
  boxes: Box[],
  { x, y, size }: { x: number; y: number; size: number },
): number => {
  let top = y;
  boxes
    .filter(
      (b) =>
        b.bottom > b.top &&
        b.right > b.left &&
        x < b.right &&
        x + size > b.left,
    )
    .sort((a, b) => b.bottom - a.bottom)
    .forEach((b) => {
      if (top < b.bottom + GAP_PX && top + size > b.top - GAP_PX) {
        top = b.top - GAP_PX - size;
      }
    });
  return Math.max(0, y - Math.max(top, TOP_MARGIN_PX));
};

interface UseComposerClearanceArgs {
  enabled: boolean;
  composerRef: RefObject<MayaAssistantComposer | null>;
  /** The launcher's stored position and size. */
  x: number;
  y: number;
  size: number;
}

/**
 * How far the launcher has to rise so it doesn't cover the AI Assistant
 * page's composer, or the form panel above it — the send button sits in the
 * launcher's usual corner. 0 when nothing is covered. Only where it is drawn
 * changes; the stored position stays, so leaving the page brings it back.
 */
export const useComposerClearance = ({
  enabled,
  composerRef,
  x,
  y,
  size,
}: UseComposerClearanceArgs): number => {
  const [lift, setLift] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const measure = () => {
      // The composer and what PromptInput draws above it (the form panel).
      const area = composerRef.current?.inputRef.current?.closest(
        "[data-composer-area]",
      );
      const boxes = area
        ? [area, ...Array.from(area.children)].map((el) =>
            el.getBoundingClientRect(),
          )
        : [];
      const next = liftToClear(boxes, { x, y, size });
      setLift((prev) => (prev === next ? prev : next));
    };
    measure();
    const timer = window.setInterval(measure, MEASURE_MS);
    window.addEventListener("resize", measure);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", measure);
    };
  }, [enabled, composerRef, x, y, size]);

  return enabled ? lift : 0;
};
